<template>
  <div class="flex flex-col gap-4" data-testid="role-import-preview">
    <!-- What the whole file adds up to, so the scale of the change is clear before the detail. -->
    <div class="flex flex-wrap items-center gap-2" data-testid="role-import-summary">
      <InfuseTag v-if="report.summary.toCreate > 0" variant="success" :inline="true" data-testid="summary-create">
        {{ t('iam.roles.import.summary.create', { count: report.summary.toCreate }) }}
      </InfuseTag>
      <InfuseTag v-if="report.summary.toUpdate > 0" variant="primary" :inline="true" data-testid="summary-update">
        {{ t('iam.roles.import.summary.update', { count: report.summary.toUpdate }) }}
      </InfuseTag>
      <InfuseTag v-if="report.summary.unchanged > 0" variant="neutral" :inline="true" data-testid="summary-unchanged">
        {{ t('iam.roles.import.summary.unchanged', { count: report.summary.unchanged }) }}
      </InfuseTag>
      <InfuseTag v-if="report.summary.blocked > 0" variant="error" :inline="true" data-testid="summary-blocked">
        {{ t('iam.roles.import.summary.blocked', { count: report.summary.blocked }) }}
      </InfuseTag>
    </div>

    <InfuseNote v-if="nothingToApply" type="info" data-testid="role-import-nothing-to-apply">
      {{ t('iam.roles.import.nothingToApply') }}
    </InfuseNote>

    <div
      v-for="(entry, index) in report.roles"
      :key="`${entry.exportedId ?? entry.exportedName}-${index}`"
      class="rounded-md border border-neutral-200"
      :class="entry.action === 'BLOCKED' ? 'border-error-200 bg-error-50' : 'bg-white'"
      :data-testid="`role-import-entry-${index}`"
    >
      <div class="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-100 px-4 py-3">
        <div class="flex flex-col">
          <span class="text-sm font-bold text-neutral-800" data-testid="role-import-entry-name">
            {{ entry.exportedName }}
          </span>
          <span class="text-xs text-neutral-500">{{ matchedByText(entry) }}</span>
        </div>

        <InfuseTag :variant="actionVariant(entry.action)" :inline="true" data-testid="role-import-entry-action">
          {{ t(`iam.roles.import.action.${entry.action}`) }}
        </InfuseTag>
      </div>

      <div class="flex flex-col gap-3 px-4 py-3">
        <!-- Anything the import would overwrite on the role itself. -->
        <dl v-if="hasFieldChanges(entry)" class="flex flex-col gap-1" data-testid="role-import-entry-fields">
          <ValueChangeRow :label="t('iam.roles.name')" :change="entry.nameChange" />
          <ValueChangeRow :label="t('iam.roles.description')" :change="entry.descriptionChange" />
          <ValueChangeRow :label="t('iam.roles.shared')" :change="entry.sharedChange" />
          <ValueChangeRow :label="t('iam.roles.import.field.unassignable')" :change="entry.unassignableChange" />
        </dl>

        <!-- Ranking is deliberately reported rather than applied, so it is called out separately. -->
        <p v-if="entry.rankingDifference" class="text-xs text-neutral-500" data-testid="role-import-entry-ranking">
          <font-awesome-icon :icon="['far', 'circle-info']" class="mr-1" />
          {{
            t('iam.roles.import.rankingNotApplied', {
              from: entry.rankingDifference.from,
              to: entry.rankingDifference.to
            })
          }}
        </p>

        <PermissionRefList
          v-if="entry.permissionsToAdd.length > 0"
          :title="t('iam.roles.import.permissionsToAdd', { count: entry.permissionsToAdd.length })"
          :permissions="entry.permissionsToAdd"
          variant="add"
          data-testid="role-import-entry-permissions-add"
        />

        <PermissionRefList
          v-if="entry.permissionsToRemove.length > 0"
          :title="t('iam.roles.import.permissionsToRemove', { count: entry.permissionsToRemove.length })"
          :permissions="entry.permissionsToRemove"
          variant="remove"
          data-testid="role-import-entry-permissions-remove"
        />

        <p v-if="entry.permissionsUnchangedCount > 0" class="text-xs text-neutral-500">
          {{ t('iam.roles.import.permissionsUnchanged', { count: entry.permissionsUnchangedCount }) }}
        </p>

        <ul v-if="entry.problems.length > 0" class="flex flex-col gap-1" data-testid="role-import-entry-problems">
          <li
            v-for="(problem, problemIndex) in entry.problems"
            :key="problemIndex"
            class="flex items-start gap-2 text-xs"
            :class="problem.severity === 'BLOCKING' ? 'text-error-700' : 'text-warning-700'"
          >
            <font-awesome-icon
              :icon="['far', problem.severity === 'BLOCKING' ? 'circle-exclamation' : 'triangle-exclamation']"
              class="mt-0.5 shrink-0"
            />
            <span>{{ problem.message }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { RoleImportAction, RoleImportEntry, RoleImportReport } from '@infuse/iam/role/type';
import { InfuseNote, InfuseTag } from '@infuse/ui/components';
import type { UiTagVariant } from '@infuse/ui/domain';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PermissionRefList from './PermissionRefList.vue';
import ValueChangeRow from './ValueChangeRow.vue';

const { t } = useI18n();

const props = defineProps<{
  report: RoleImportReport;
}>();

const nothingToApply = computed(
  () => props.report.summary.toCreate === 0 && props.report.summary.toUpdate === 0
);

function hasFieldChanges(entry: RoleImportEntry): boolean {
  return !!(entry.nameChange || entry.descriptionChange || entry.sharedChange || entry.unassignableChange);
}

function actionVariant(action: RoleImportAction): UiTagVariant {
  switch (action) {
    case 'CREATE':
      return 'success';
    case 'UPDATE':
      return 'primary';
    case 'BLOCKED':
      return 'error';
    default:
      return 'neutral';
  }
}

function matchedByText(entry: RoleImportEntry): string {
  switch (entry.matchedBy) {
    case 'ID':
      return t('iam.roles.import.matchedBy.id');
    case 'NAME':
      return t('iam.roles.import.matchedBy.name');
    default:
      return t('iam.roles.import.matchedBy.none');
  }
}
</script>
